import {useEffect, useMemo, useState, useRef} from 'react';
import useUserQuery from 'src/hooks/queries/use-user-query';
import {isEqual} from 'lodash';
import {SocialPlatform} from '@/types/platforms';
import useUpdateBirthdate from '@/hooks/queries/mutations/use-update-birthdate';
import {User, UserSchema} from '@/schema/User';
import {providerToPlatformMap} from '@/config/constants';

export type UserDataCallbacks = {
  onAvatarUrlChanged?: (url: string | undefined) => void;
  onConnectedPlatformsChanged?: (platforms: SocialPlatform[]) => void;
  onUserChanged?: (user: User) => void;
};

export function useUserData(callbacks: UserDataCallbacks) {
  // Use ref to store stable callback references
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const [birthDateUpdateError, setBirthDateUpdateError] = useState<
    string | null
  >(null);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [connectedPlatforms, setConnectedPlatforms] = useState<
    SocialPlatform[]
  >([]);
  const [platformsInitialized, setPlatformsInitialized] = useState(false);

  const updateBirthdayMutation = useUpdateBirthdate({
    onError: () => {
      setBirthDateUpdateError('Failed to update birthday. Please try again.');
    },
  });

  const {
    user: queryUser,
    isLoading,
    isError,
  } = useUserQuery<User>({
    include: ['profile.profile_progress_report'],
  });

  const user = useMemo(() => {
    const parsedUser = UserSchema.safeParse(queryUser);
    return parsedUser.success ? parsedUser.data : undefined;
  }, [queryUser]);

  useEffect(() => {
    if (user) {
      callbacksRef.current.onUserChanged?.(user);
    }
  }, [user]);

  useEffect(() => {
    if (!isLoading && !isError && user) {
      const identities = user.identities || [];
      const platforms = identities
        .map((identity) => {
          const {provider} = identity;
          if (isValidProvider(provider)) {
            return providerToPlatformMap[provider];
          }
          return null;
        })
        .filter(
          (platform): platform is NonNullable<SocialPlatform> =>
            platform !== null
        );

      setConnectedPlatforms((prevPlatforms) => {
        // Only update if platforms have changed
        if (!isEqual(prevPlatforms, platforms)) {
          callbacksRef.current.onConnectedPlatformsChanged?.(platforms);
          return platforms;
        }
        return prevPlatforms;
      });

      if (!platformsInitialized) {
        setPlatformsInitialized(true);
      }

      const identityWithAvatar = identities.find(
        ({cachedProfileImageUrl}) => cachedProfileImageUrl
      );
      if (identityWithAvatar?.cachedProfileImageUrl) {
        setAvatarUrl((prevAvatarUrl) => {
          if (!prevAvatarUrl) {
            const newAvatarUrl =
              identityWithAvatar.cachedProfileImageUrl || undefined;
            callbacksRef.current.onAvatarUrlChanged?.(newAvatarUrl);
            return newAvatarUrl;
          }
          return prevAvatarUrl;
        });
      }
    }
  }, [isError, isLoading, user, platformsInitialized]);

  return {
    user,
    isLoading,
    isError,
    avatarUrl,
    connectedPlatforms,
    isPlatformsInitialized: platformsInitialized,
    updateBirthday: updateBirthdayMutation.mutate,
    isLoadingBirthday: updateBirthdayMutation.isLoading,
    birthdayError: birthDateUpdateError,
    userBirthDate: user?.birthDate,
  };
}

function isValidProvider(
  provider: string
): provider is keyof typeof providerToPlatformMap {
  return provider in providerToPlatformMap;
}
